import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    LOCALE_ID,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { Media } from '@klickdata/core/media/src/media.model';
import { PlayerMediaViewComponent } from '@klickdata/core/media/src/player-media-view/player-media-view.component';
import { SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceOpportunity } from '@klickdata/core/opportunity';
import { AppScope, ResourceTypes } from '@klickdata/core/resource';
import { ResourceItem } from '@klickdata/core/resource-item';
import { Resource } from '@klickdata/core/resource/src/resource.model';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { ExitService } from '@klickdata/player/src/core/exit/exit.service';
import { OccasionService } from '@klickdata/player/src/core/occasion/occasion.service';
import { PlayerItem } from '@klickdata/player/src/core/player/player-item';
import { PlayerService } from '@klickdata/player/src/core/player/player.service';
import { ResourceItemOpportunityService } from '@klickdata/player/src/core/resource-item/resource-item.service';
import { CourseItemsActionsOnNext, TakeResActionValue } from '@klickdata/player/src/next-step.service';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, first, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-material-player-shared',
    templateUrl: './material-player-shared.component.html',
    styleUrls: ['./material-player-shared.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialPlayerSharedComponent extends OnDestroyHandler implements OnInit {
    public user: User;
    @Input() materialResource: Resource;
    @Input() materialOccasion: ResourceOpportunity;
    @Input() isMobile: boolean;
    @Input() actionBtn: TemplateRef<any>;
    @ViewChild(PlayerMediaViewComponent) mediaView: PlayerMediaViewComponent;
    public materialItemOccasion$: Observable<ResourceOpportunity>;
    public materialItem$: Observable<ResourceItem>;
    public loadingNavigation: Subject<boolean> = new Subject<boolean>();
    public closeEnabled: boolean;
    private playerSub: Subscription;
    public nextBtnClickedInPDF: boolean;
    ResourceTypes = ResourceTypes;
    public items$: Observable<ResourceItem[]>;
    currentIndex = 0;
    public currentMedia: Media;
    MediaType = MediaType;
    public materialWithOptions: boolean;
    private customer: Customer;
    public authUser$: Observable<User>;
    public startingResizing = false;

    constructor(
        protected itemService: ResourceItemOpportunityService,
        protected occasionService: OccasionService,
        protected playerService: PlayerService,
        protected route: ActivatedRoute,
        protected changeRef: ChangeDetectorRef,
        protected router: Router,
        protected exitService: ExitService,
        protected auth: AuthService,
        @Inject(LOCALE_ID) protected localeId: string
    ) {
        super();
        this.localeId = localeId === 'en' ? 'en-US' : localeId;
        this.auth
            .getCustomer()
            .pipe(takeUntil(this.destroy))
            .subscribe((customer) => (this.customer = customer));
    }

    ngOnInit(): void {
        this.initializeItemsObservable();
        this.authUser$ = this.auth.getUser().pipe(shareReplay());
    }

    private initializeItemsObservable(): void {
        this.items$ = Utils.getRouteParam(this.route, 'itemOccId').pipe(
            switchMap((itemOccId) =>
                this.occasionService
                    .getOccasion(itemOccId)
                    .pipe(
                        tap(
                            (itemOccasion) =>
                                (this.materialItem$ = this.itemService.getItem(
                                    itemOccasion.resource_item_id,
                                    itemOccasion.id
                                ))
                        )
                    )
            ),
            switchMap((itemOccasion) => {
                this.materialItemOccasion$ = of(itemOccasion);
                return this.itemService.getAllItems(this.materialResource.id, this.materialOccasion.id, true).pipe(
                    map((items) => {
                        this.currentIndex =
                            items.findIndex((item) => item.item_opportunity_id === itemOccasion.id) ?? 0;
                        return items;
                    })
                );
            })
        );
    }

    goToIndex(index: number, materialItemOccasion: ResourceOpportunity, items: ResourceItem[]) {
        this.loadingNavigation.next(true);
        this.occasionService
            .done(materialItemOccasion)
            .pipe(
                takeUntil(this.destroy),
                switchMap(() => {
                    const item = items[index];
                    return item.item_opportunity_id
                        ? of(item.item_opportunity_id)
                        : this.occasionService
                              .createMaterialItemOccasion(item.id, this.materialOccasion.id)
                              .pipe(map((occ) => occ.id));
                })
            )
            .subscribe((nextOpp) => {
                this.loadingNavigation.next(false);
                this.router
                    .navigate(['../', nextOpp], {
                        relativeTo: this.route,
                        queryParamsHandling: 'preserve',
                    })
                    .then(() => {
                        // Refresh the items$ observable after navigation
                        setTimeout(() => {
                            this.initializeItemsObservable();
                        }, 500);
                    });
            });
    }
    public onExit(): void {
        this.exitService.exit();
    }
    onMediaUpdated(media: Media) {
        this.currentMedia = media;
        this.materialWithOptions =
            media.mediaType == MediaType.VIDEO ||
            media.mediaType == MediaType.PDF ||
            media.mediaType == MediaType.URL ||
            media.mediaType == MediaType.AUDIO ||
            media.mediaType == MediaType.LINK;
        this.changeRef.markForCheck();
    }
    public get mediaPlayer() {
        return this.mediaView?.player;
    }
    public openInNewTab() {
        window.open(this.currentMedia.url || this.currentMedia.src, '_blank');
    }
    public onClosing(user: User) {
        const actions: CourseItemsActionsOnNext[] = [
            {
                label: $localize`Yes`,
                value: TakeResActionValue.CONTINUE,
                class: 'positive',
                customBtn: false,
            },
            {
                label: $localize`Continue later`,
                value: TakeResActionValue.CONTINUE_LATER,
                class: 'custom',
                customBtn: false,
            },
            { label: $localize`Close`, value: TakeResActionValue.CANCEL, class: 'negative', customBtn: false },
        ];
        if (this.mediaView && this.mediaView.player && !this.mediaView.player.ended) {
            this.playerService.updateSliderData({
                dataType: user.canPlayAsGroupLeader()
                    ? SideNaveDataTypes.FINISH_WORK_WITH_GROUP
                    : SideNaveDataTypes.GENERAL_NOTIFIER,
                data: {
                    icon: 'fact_check',
                    title: $localize`:@@Areyoudone:Are you done?`,
                    user: user,
                    resItemName: this.getResItemNameTranslation(),
                    contentBody: $localize`Do you want to mark this ${this.getResItemNameTranslation()} as completed?`,
                    actions: actions,
                    occasion: this.materialOccasion,
                },
            });
            this.playerService
                .getSideNavResponseData()
                .pipe(
                    takeUntil(this.destroy),
                    filter((res) => !!res),
                    distinctUntilChanged(),
                    switchMap((res) =>
                        res == TakeResActionValue.CONTINUE_LATER
                            ? this.updatePlayer()
                            : res.action == TakeResActionValue.GROUP_WORK_DONE
                            ? res.decision == 'done'
                                ? this.saveChildOccasion().pipe(
                                      switchMap(() =>
                                          this.saveGroupWork(res.userIds, res.decision == 'done', res.timeSpend)
                                      )
                                  )
                                : this.updatePlayer().pipe(switchMap(() => this.saveGroupWork(res.userIds, false)))
                            : this.saveChildOccasion().pipe(
                                  switchMap(() => this.occasionService.done(this.materialOccasion))
                              )
                    )
                )
                .subscribe(() => this.exitService.exitToAUActivity(this.materialOccasion?.id));
        } else {
            const desc = this.getPDFPagesMsg()
                ? this.getPDFPagesMsg()
                : $localize`Do you want to mark this ${this.getResItemNameTranslation()} as completed?`;

            this.playerService.updateSliderData({
                dataType: user.canPlayAsGroupLeader()
                    ? SideNaveDataTypes.FINISH_WORK_WITH_GROUP
                    : SideNaveDataTypes.GENERAL_NOTIFIER,
                data: {
                    icon: 'fact_check',
                    title: $localize`:@@Areyoudone:Are you done?`,
                    user: user,
                    resItemName: this.getResItemNameTranslation(),
                    contentBody: desc,
                    actions: actions,
                    occasion: this.materialOccasion,
                },
            });
            this.playerService
                .getSideNavResponseData()
                .pipe(
                    takeUntil(this.destroy),
                    distinctUntilChanged(),
                    filter((res) => !!res),
                    switchMap((res) =>
                        res == TakeResActionValue.CONTINUE_LATER
                            ? of(null)
                            : res.action == TakeResActionValue.GROUP_WORK_DONE
                            ? this.saveChildOccasion().pipe(
                                  switchMap(() =>
                                      this.saveGroupWork(res.userIds, res.decision == 'done', res.timeSpend)
                                  )
                              )
                            : this.saveChildOccasion().pipe(
                                  switchMap(() => this.occasionService.done(this.materialOccasion))
                              )
                    )
                )
                .subscribe(() => this.exitService.exitToAUActivity(this.materialOccasion?.id));
        }
    }
    private saveChildOccasion(): Observable<ResourceOpportunity> {
        return this.materialItemOccasion$.pipe(
            switchMap((materialItemOccasion) => this.occasionService.done(materialItemOccasion))
        );
    }
    private saveGroupWork(userIds: number[], isDone: boolean, timeSpend?: number): Observable<ResourceOpportunity> {
        return this.occasionService.doneGroupWork(this.materialOccasion.id, userIds, isDone, timeSpend);
    }
    private updatePlayer(): Observable<PlayerItem> {
        return this.playerService.update(this.materialOccasion.id, {
            position: this.getCurrentPlayerTime(),
            video_id: this.materialOccasion.resource_id,
            video_type: this.mediaType,
        });
    }
    getPDFPagesMsg() {
        if (this.mediaView && this.mediaView.isPDFLoaded && this.mediaView.totalPages > this.mediaView.page) {
            return $localize`You only completed ${this.mediaView.page} pages out of ${
                this.mediaView.totalPages
            } pages for this PDF. Do you want to mark this ${this.getResItemNameTranslation()} as completed?`;
        }
        return null;
    }
    showDesc() {
        this.playerService.updateSliderData({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'description',
                title: $localize`Description`,
                secContentBody: this.materialResource.description,
                positiveBtn: $localize`Ok`,
            },
        });
    }
    public onPlayerReady() {
        if (!this.playerSub && this.mediaView && this.mediaView.player) {
            this.setCloseEnable(false);
            this.playerSub = of(this.materialOccasion)
                .pipe(
                    first(),
                    switchMap((opportunity) =>
                        this.playerService.start({
                            artnr: this.materialResource.article_code,
                            video_id: opportunity.resource_id,
                            parent_id: opportunity.parent_id,
                            video_type: this.mediaType,
                        })
                    )
                )
                .subscribe((playerItem: PlayerItem) => {
                    if (playerItem.position > 0) {
                        this.resumeMaterial(+playerItem.position);
                    } else {
                        this.setCloseEnable(true);
                    }
                });
        }
    }

    private resumeMaterial(currentTime: number) {
        this.playerService.updateSliderData({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'play_arrow',
                title: $localize`Resume ${this.getResItemNameTranslation()}`,
                contentBody: $localize`Do you want to resume the ${this.getResItemNameTranslation()} or start over?`,
                positiveBtn: $localize`Resume`,
                customBtn: $localize`Start Over`,
            },
        });
        this.playerService
            .getSideNavAction()
            .pipe(
                takeUntil(this.destroy),
                filter((action) => !!action)
            )
            .subscribe((action) => {
                this.setCloseEnable(true);
                this.mediaView.player.play(action == SideNaveActionsTypes.POSITIVE ? currentTime : 0);
            });
    }

    public onMediaCompleted(status: boolean, materialItemOccasion: ResourceOpportunity, items: ResourceItem[]) {
        if (this.mediaView && this.mediaView.player && items[this.currentIndex + 1]) {
            // TODO when video  completed
            this.goToIndex(this.currentIndex + 1, materialItemOccasion, items);
        } else if (this.materialResource?.type_id === ResourceTypes.WebsiteMaterial) {
            // TODO when website
        } else {
            this.nextBtnClickedInPDF ? this.setCloseEnable(true) : this.setCloseEnable(status);
        }
    }

    public setCloseEnable(status: boolean) {
        setTimeout(() => {
            this.closeEnabled = true;
            this.changeRef.markForCheck();
        }, 500);
    }

    public getCurrentPlayerTime(): any {
        return !this.mediaView.player.ended ? this.mediaView.player.currentTime : 0;
    }

    ngOnDestroy() {
        if (this.playerSub) {
            this.playerSub.unsubscribe();
            this.playerSub = null;
        }
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    public styleInnerHTML(description) {
        if (description) {
            return '<div class="inner-HTML">' + description + '</div>';
        } else {
            return;
        }
    }

    get mediaType(): string {
        return this.mediaView && this.mediaView.player ? this.mediaView.player.mediaType : 'video';
    }
    getResItemNameTranslation() {
        return (
            this.materialResource?.collaboration?.view?.metadata?.name_translation?.[this.localeId] ??
            this.customer.scope_names[AppScope.MATERIAL] ??
            AppScope.label(AppScope.MATERIAL)
        );
    }
}
